import type { StageItemView, StageListing as StageListingData } from "@fieldpath/shared";
import { mdyDay } from "@fieldpath/shared";
import { useState } from "react";

/**
 * A stage's OBJECT LISTING (E1): the figures GIS draws, the records being
 * filed. Each item is chosen from the catalog or typed, is ticked when it is
 * produced, and says where it was saved.
 *
 * Every control here is offered only where the server's `mayAdd` / `mayTick`
 * say so — the same answers the writes enforce with (`db/stage-items.ts`):
 * adding is a window that closes once the stage has passed, ticking a floor
 * that opens once it is reached. A ticked item has no remove: it is produced
 * work with an author.
 *
 * FIGURES ARE NUMBERED (F101, 30 Oct 2026): "Fig. 1" before each name, in
 * number order. Whoever `mayNumber` -- the task's PM / Task Manager or an
 * admin -- picks another number from a list; one already taken swaps.
 *
 * Props only, no fetcher: the task page does the calls, so this renders in a
 * harness as it does on the page.
 */
export function StageListing({
  listing,
  stageName,
  busy,
  onAdd,
  onRemove,
  onSet,
  onNumber,
}: {
  listing: StageListingData;
  stageName: string;
  busy: boolean;
  onAdd: (input: { typeId?: string; name?: string }) => void;
  onRemove: (itemId: string) => void;
  onSet: (itemId: string, input: { done: boolean; filePath?: string | null; note?: string | null }) => void;
  onNumber: (itemId: string, number: number) => void;
}) {
  const [typeId, setTypeId] = useState("");
  const [custom, setCustom] = useState("");
  const done = listing.items.filter((i) => i.completedAt !== null).length;
  // The numbers offered: every one in use, and enough for each figure to
  // have its own.
  const highest = Math.max(listing.items.length, ...listing.items.map((i) => i.number ?? 0));
  const numbers = Array.from({ length: highest }, (_, i) => i + 1);

  return (
    <section className="listing">
      <div className="listing-head">
        <span className="listing-name">{listing.name}</span>
        <span className="listing-count">
          {listing.items.length === 0
            ? "none listed yet"
            : `${done} of ${listing.items.length} done`}
        </span>
      </div>

      {listing.items.length > 0 ? (
        <ul className="listing-items">
          {listing.items.map((item) => (
            <ListingItem
              key={item.id}
              item={item}
              mayTick={listing.mayTick}
              mayRemove={listing.mayAdd && item.completedAt === null}
              numbers={listing.mayNumber ? numbers : null}
              busy={busy}
              onRemove={() => onRemove(item.id)}
              onSet={(input) => onSet(item.id, input)}
              onNumber={(n) => onNumber(item.id, n)}
            />
          ))}
        </ul>
      ) : null}

      {listing.mayAdd ? (
        <div className="listing-add">
          {listing.options.length > 0 ? (
            <>
              <select
                aria-label={`Add to ${listing.name}`}
                value={typeId}
                onChange={(e) => setTypeId(e.target.value)}
                disabled={busy}
              >
                <option value="">Choose from the list…</option>
                {listing.options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
              <button
                className="secondary"
                disabled={busy || typeId === ""}
                onClick={() => {
                  onAdd({ typeId });
                  setTypeId("");
                }}
              >
                Add
              </button>
              <span className="muted">or</span>
            </>
          ) : null}
          <input
            aria-label={`A custom item for ${listing.name}`}
            placeholder="A custom item"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            disabled={busy}
          />
          <button
            className="secondary"
            disabled={busy || custom.trim() === ""}
            onClick={() => {
              onAdd({ name: custom.trim() });
              setCustom("");
            }}
          >
            Add
          </button>
        </div>
      ) : null}

      {/* Why a control is missing, rather than a blank where it was. */}
      {!listing.mayAdd && listing.mayTick ? (
        <p className="muted listing-why">
          {stageName} has been passed, so nothing more is added here — anything
          missing is new work.
        </p>
      ) : null}
      {listing.mayAdd && !listing.mayTick && listing.items.length > 0 ? (
        <p className="muted listing-why">Items are marked done once the work reaches {stageName}.</p>
      ) : null}
    </section>
  );
}

function ListingItem({
  item,
  mayTick,
  mayRemove,
  numbers,
  busy,
  onRemove,
  onSet,
  onNumber,
}: {
  item: StageItemView;
  mayTick: boolean;
  mayRemove: boolean;
  /** The numbers to choose from, or null when this person may not renumber. */
  numbers: number[] | null;
  busy: boolean;
  onRemove: () => void;
  onSet: (input: { done: boolean; filePath?: string | null; note?: string | null }) => void;
  onNumber: (number: number) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [filePath, setFilePath] = useState(item.filePath ?? "");
  const [note, setNote] = useState(item.note ?? "");
  const isDone = item.completedAt !== null;
  // Only http(s) becomes a link -- the same rule as instructions. A drive
  // path stays text: a browser cannot open Z:\ and a dead link reads broken.
  const link = item.filePath && /^https?:\/\//i.test(item.filePath) ? item.filePath : null;

  return (
    <li className={`listing-item${isDone ? " done" : ""}`}>
      <div className="listing-lead">
        {/* Outside the label: a click on the number must not tick the figure. */}
        {item.number !== null && numbers ? (
          <select
            className="listing-num"
            aria-label={`Figure number for ${item.name}`}
            value={item.number}
            disabled={busy}
            onChange={(e) => onNumber(Number(e.target.value))}
          >
            {numbers.map((n) => (
              <option key={n} value={n}>
                Fig. {n}
              </option>
            ))}
          </select>
        ) : item.number !== null ? (
          <span className="listing-num">Fig. {item.number}</span>
        ) : null}
        <label className="listing-tick">
          <input
            type="checkbox"
            checked={isDone}
            disabled={busy || !mayTick}
            onChange={(e) => onSet({ done: e.target.checked })}
          />
          <span className="listing-item-name">{item.name}</span>
          {item.typeId === null ? <span className="listing-custom">CUSTOM</span> : null}
        </label>
      </div>
      <span className="listing-meta">
        {isDone ? (
          <span className="muted">
            {item.completedByName ?? "Someone"}, {mdyDay(item.completedAt!)}
          </span>
        ) : null}
        {link ? (
          <a href={link} target="_blank" rel="noreferrer">
            Open
          </a>
        ) : item.filePath ? (
          <span className="listing-path" title={item.filePath}>
            {item.filePath}
          </span>
        ) : null}
        {mayTick && !editing ? (
          <button className="linkish" onClick={() => setEditing(true)} disabled={busy}>
            {item.filePath || item.note ? "Edit where" : "Say where"}
          </button>
        ) : null}
        {mayRemove ? (
          <button className="linkish" onClick={onRemove} disabled={busy} title="Not needed">
            Remove
          </button>
        ) : null}
      </span>
      {item.note && !editing ? <div className="listing-note">{item.note}</div> : null}
      {editing ? (
        <div className="listing-edit">
          <input
            aria-label={`Where ${item.name} is saved`}
            placeholder="Egnyte link or file path"
            value={filePath}
            onChange={(e) => setFilePath(e.target.value)}
          />
          <input
            aria-label={`A note on ${item.name}`}
            placeholder="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <button
            className="primary"
            disabled={busy}
            onClick={() => {
              onSet({ done: isDone, filePath, note });
              setEditing(false);
            }}
          >
            Save
          </button>
          <button onClick={() => setEditing(false)} disabled={busy}>
            Cancel
          </button>
        </div>
      ) : null}
    </li>
  );
}
